import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

const SettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser, changePassword, deleteAccount, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();

  const [firstname, setFirstname] = useState(user?.firstname || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await updateUser({ firstname: firstname });
      setSuccess(t('common.success'));
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 8) {
      setError(t('errors.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('errors.passwordMismatch'));
      return;
    }

    setIsLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(t('auth.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsLoading(true);
    try {
      await deleteAccount();
      navigate('/login');
    } catch (err) {
      setError(err.message || t('errors.serverError'));
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div>
      <h1 className="page-title">{t('settings.title')}</h1>

      {error && (
        <div className="form-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ color: 'var(--color-success)', marginBottom: '1rem', textAlign: 'center' }}>
          {success}
        </div>
      )}

      {/* Theme */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>{t('settings.theme')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className={`btn ${theme === 'light' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTheme('light')}
          >
            {t('settings.lightMode')}
          </button>
          <button
            className={`btn ${theme === 'dark' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTheme('dark')}
          >
            {t('settings.darkMode')}
          </button>
        </div>
      </div>

      {/* Language */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>{t('settings.language')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className={`btn ${language === 'de' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setLanguage('de')}
          >
            {t('settings.german')}
          </button>
          <button
            className={`btn ${language === 'en' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setLanguage('en')}
          >
            {t('settings.english')}
          </button>
        </div>
      </div>

      {/* Profile */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>{t('settings.profile')}</h2>
        <form onSubmit={handleUpdateProfile}>
          <div className="form-group">
            <label className="form-label">{t('auth.email')}</label>
            <input
              type="email"
              className="form-input"
              value={user?.email || ''}
              disabled
            />
          </div>
          <div className="form-group">
            <label htmlFor="firstname" className="form-label">
              {t('auth.firstname')}
            </label>
            <input
              type="text"
              id="firstname"
              className="form-input"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {t('common.save')}
          </button>
        </form>
      </div>

      {/* Change Password */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>{t('settings.changePassword')}</h2>
        <form onSubmit={handleChangePassword}>
          <div className="form-group">
            <label htmlFor="currentPassword" className="form-label">
              {t('settings.currentPassword')}
            </label>
            <input
              type="password"
              id="currentPassword"
              className="form-input"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              {t('auth.newPassword')}
            </label>
            <input
              type="password"
              id="newPassword"
              className="form-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              {t('auth.confirmPassword')}
            </label>
            <input
              type="password"
              id="confirmPassword"
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {t('settings.changePassword')}
          </button>
        </form>
      </div>

      {/* Logout */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <button className="btn btn-outline btn-block" onClick={handleLogout}>
          {t('auth.logout')}
        </button>
      </div>

      {/* Delete Account */}
      <div className="card" style={{ borderColor: 'var(--color-error)' }}>
        <h2 style={{ marginBottom: '1rem', color: 'var(--color-error)' }}>
          {t('settings.deleteAccount')}
        </h2>
        <button
          className="btn btn-error"
          onClick={() => setShowDeleteConfirm(true)}
        >
          {t('settings.deleteAccount')}
        </button>
      </div>

      {/* Copyright Notice */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        textAlign: 'center',
        color: 'var(--color-text-secondary)',
        fontSize: '0.875rem'
      }}>
        <img
          src="/sts-logo.png"
          alt="STS Logo"
          width="48"
          height="48"
          style={{ marginBottom: '0.5rem', borderRadius: '50%' }}
        />
        <p style={{ margin: 0 }}>Copyright 2026 Thomas Menath - STS</p>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title" style={{ color: 'var(--color-error)' }}>
              {t('settings.deleteAccount')}
            </h2>
            <p>{t('settings.confirmDeleteAccount')}</p>
            <div className="modal-actions">
              <button
                className="btn btn-outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-error"
                onClick={handleDeleteAccount}
                disabled={isLoading}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
